import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Card, CardContent } from './ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import api from '../lib/api';
import DetailSearchSort, { filterAndSortRows } from './DetailSearchSort';

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const SIZE_ORDER = ['S','M','L','XL','2XL','3XL','4XL','5XL','6XL','7XL','8XL','9XL','Free Size','32','34','36','38','40','42'];
const fmtDate = (d) => { if (!d) return ''; const p = d.split('-'); return p.length === 3 && p[0].length === 4 ? `${p[2]}-${p[1]}-${p[0].slice(-2)}` : d; };
const STATUS_COLORS = { pending: 'bg-yellow-100 text-yellow-700', approved: 'bg-green-100 text-green-700', processed: 'bg-blue-100 text-blue-700', cancelled: 'bg-red-100 text-red-700' };
const statusLabel = (s) => (s || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

const PurchaseReturnDetail = ({ user }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [pr, setPr] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lightboxImg, setLightboxImg] = useState(null);
  const [searchDesign, setSearchDesign] = useState('');
  const [sortBy, setSortBy] = useState('');
  const [shareOpen, setShareOpen] = useState(false);
  const [shareFormat, setShareFormat] = useState('pdf');

  const canManage = user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'manager';

  const loadData = useCallback(async () => {
    try {
      const [prRes, vendRes, prodRes] = await Promise.all([
        api.get(`/purchase-returns/${id}`), api.get('/vendors'), api.get('/products'),
      ]);
      setPr(prRes.data); setVendors(vendRes.data); setProducts(prodRes.data);
    } catch { toast.error('Failed to load PR'); navigate('/purchase-returns'); }
    finally { setLoading(false); }
  }, [id, navigate]);

  useEffect(() => { loadData(); }, [loadData]);

  const getVendorName = (vid) => { const v = vendors.find(x => x.id === vid); if (!v) return 'Unknown'; const loc = v.city?.toLowerCase() === 'mumbai' ? v.area : v.city; return loc ? `${v.name} (${loc})` : v.name; };
  const getProductImages = (pid) => products.find(p => p.id === pid)?.images || [];

  const totalQty = (pr?.items || []).reduce((s, i) => s + (i.variants || []).reduce((vs, v) => vs + (v.qty_returned || 0), 0), 0);
  const totalAmount = pr?.total_value || 0;

  const deletePR = async () => {
    if (!window.confirm('Delete this Purchase Return? Stock will be reversed.')) return;
    try { await api.delete(`/purchase-returns/${pr.id}`); toast.success('PR deleted'); navigate('/purchase-returns'); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const executeShare = async (format) => {
    const endpoint = format === 'pdf' ? `/purchase-returns/${pr.id}/pdf` : `/purchase-returns/${pr.id}/excel`;
    const mimeType = format === 'pdf' ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
    const ext = format === 'pdf' ? '.pdf' : '.xlsx';
    try {
      const res = await api.get(endpoint, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: mimeType }));
      const a = document.createElement('a'); a.href = url; a.download = `${pr.debit_note_number}${ext}`; a.click();
      window.URL.revokeObjectURL(url);
      toast.success(`${format.toUpperCase()} downloaded!`);
    } catch { toast.error('Failed'); }
    setShareOpen(false);
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-4 border-emerald-600 border-t-transparent rounded-full animate-spin"></div></div>;
  if (!pr) return null;

  const allItems = pr.items || [];
  const usedSizes = new Set();
  allItems.forEach(item => (item.variants || []).forEach(v => usedSizes.add(v.size)));
  const activeSizes = SIZE_ORDER.filter(s => usedSizes.has(s));

  let srNum = 0, grandQty = 0;
  const rows = [];
  allItems.forEach((item) => {
    const colorMap = {}, colorOrder = [];
    (item.variants || []).forEach(v => {
      const c = v.color || 'Other';
      if (!colorMap[c]) { colorMap[c] = { sizes: {}, qty: 0, reasons: {}, rates: {} }; colorOrder.push(c); }
      colorMap[c].sizes[v.size] = (colorMap[c].sizes[v.size] || 0) + (v.qty_returned || 0);
      colorMap[c].qty += (v.qty_returned || 0);
      colorMap[c].reasons[v.size] = v.reason || 'defective';
      colorMap[c].rates[v.size] = v.rate || v.cost || 0;
    });
    colorOrder.sort((a, b) => (a || '').localeCompare(b || '')).forEach(color => {
      srNum++;
      const d = colorMap[color];
      grandQty += d.qty;
      // Group by rate for merged cells (like SE @₹ pattern)
      const rateGroups = [];
      let currentRateGroup = null;
      activeSizes.forEach(s => {
        if ((d.sizes[s] || 0) === 0) return;
        const rate = d.rates[s] || 0;
        if (currentRateGroup && currentRateGroup.rate === rate) { currentRateGroup.sizes.push(s); }
        else { currentRateGroup = { rate, sizes: [s] }; rateGroups.push(currentRateGroup); }
      });
      rows.push({ sr: srNum, item, color, sizes: d.sizes, reasons: d.reasons, rates: d.rates, qty: d.qty, rateGroups });
    });
  });

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6 animate-fadeIn" data-testid="pr-detail-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="pr-detail-back" className="text-gray-500 hover:text-gray-900">
            <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" /></svg>Back
          </Button>
          <div>
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="text-2xl font-bold text-gray-900 font-mono" data-testid="pr-detail-number">{pr.debit_note_number}</h1>
              {pr.status && <Badge className={`${STATUS_COLORS[pr.status] || 'bg-gray-100'} text-sm`} data-testid="pr-detail-status">{statusLabel(pr.status)}</Badge>}
            </div>
            <h2 className="text-xl font-bold text-blue-800 mt-1" data-testid="pr-detail-vendor">{getVendorName(pr.vendor_id)}</h2>
            <div className="flex items-center gap-3 flex-wrap mt-1 text-sm text-gray-500">
              <span>{fmtDate(pr.return_date)}</span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button size="sm" variant="outline" onClick={() => setShareOpen(true)} data-testid="pr-detail-share">
            <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>Download
          </Button>
          {canManage && (
            <>
              <Button size="sm" variant="outline" className="text-blue-600 border-blue-300" onClick={() => navigate('/create-purchase-return', { state: { editPR: pr } })} data-testid="pr-detail-edit">
                <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>Edit
              </Button>
              <Button size="sm" variant="outline" className="text-red-600 border-red-300" onClick={deletePR} data-testid="pr-detail-delete">
                <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>Delete
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="pr-detail-summary">
        <Card><CardContent className="p-4 text-center"><p className="text-xs text-gray-500 mb-1">Debit Amount</p><p className="text-xl font-bold text-gray-900">₹{totalAmount.toLocaleString('en-IN')}</p></CardContent></Card>
        <Card><CardContent className="p-4 text-center"><p className="text-xs text-gray-500 mb-1">Total Returned</p><p className="text-xl font-bold text-red-600">{totalQty} pcs</p></CardContent></Card>
        <Card><CardContent className="p-4 text-center"><p className="text-xs text-gray-500 mb-1">Items</p><p className="text-xl font-bold text-emerald-700">{allItems.length}</p></CardContent></Card>
        <Card><CardContent className="p-4 text-center"><p className="text-xs text-gray-500 mb-1">Source GRN</p><p className="text-lg font-bold text-indigo-600 cursor-pointer hover:underline" onClick={() => { if (pr.source_entry_id) navigate(`/purchase-entry/${pr.source_entry_id}`); }}>{pr.source_entry_id ? 'View PE' : 'None'}</p></CardContent></Card>
      </div>

      {/* Info */}
      {(pr.notes || pr.created_at || pr.freight_charges > 0) && (
        <div className="flex gap-6 flex-wrap text-sm text-gray-600 bg-gray-50 p-3 rounded-lg">
          {pr.created_at && <span>Created: <strong>{new Date(pr.created_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</strong></span>}
          {pr.notes && <span className="italic text-gray-500">Notes: {pr.notes}</span>}
          {pr.freight_charges > 0 && <span>Freight: <strong>₹{pr.freight_charges.toLocaleString('en-IN')}</strong></span>}
        </div>
      )}

      {/* Pivot Table */}
      <Card data-testid="pr-detail-items">
        <CardContent className="p-0">
          <DetailSearchSort search={searchDesign} setSearch={setSearchDesign} sortBy={sortBy} setSortBy={setSortBy} sortOptions={['amount']} testIdPrefix="pr-detail" />
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse" data-testid="pr-excel-table">
              <thead>
                <tr className="bg-[#1E1E23] text-white text-xs uppercase tracking-wider">
                  <th className="px-3 py-2.5 text-center font-medium border border-gray-700 w-10">SR</th>
                  <th className="px-2 py-2.5 text-center font-medium border border-gray-700 w-16">Image</th>
                  <th className="px-3 py-2.5 text-left font-medium border border-gray-700">Design</th>
                  <th className="px-3 py-2.5 text-left font-medium border border-gray-700">Colour</th>
                  {activeSizes.map(s => <th key={s} className="px-2 py-2.5 text-center font-medium border border-gray-700 min-w-[42px]">{s}</th>)}
                  <th className="px-3 py-2.5 text-center font-medium border border-gray-700">Qty</th>
                </tr>
              </thead>
              <tbody>
                {filterAndSortRows(rows, searchDesign, sortBy).map((r, ri) => {
                  const images = getProductImages(r.item.product_id);
                  const firstImg = images[0];
                  return (
                    <React.Fragment key={ri}>
                      <tr className="bg-white hover:bg-red-50/40 transition-colors">
                        <td className="px-3 py-2 text-center text-gray-500 border border-gray-500 font-mono text-xs" rowSpan="2">{r.sr}</td>
                        <td className="px-2 py-1.5 text-center border border-gray-500" rowSpan="2">
                          {firstImg ? <img src={`${API_BASE}${firstImg}`} alt={r.item.design_code} className="w-11 h-11 rounded object-cover cursor-pointer hover:ring-2 hover:ring-red-400 transition-all mx-auto" onClick={() => setLightboxImg(`${API_BASE}${firstImg}`)} /> : <div className="w-11 h-11 bg-gray-100 rounded flex items-center justify-center text-gray-400 mx-auto"><svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg></div>}
                        </td>
                        <td className="px-3 py-2 font-semibold text-gray-900 border border-gray-500" rowSpan="2">{r.item.design_code}</td>
                        <td className="px-3 py-2 font-medium text-gray-800 border border-gray-500" rowSpan="2">{r.color}</td>
                        {activeSizes.map(s => {
                          const q = r.sizes[s] || 0;
                          return <td key={s} className="px-2 py-2 text-center border border-gray-500">{q > 0 ? <span className="font-medium text-red-700">{q}</span> : <span className="text-gray-300">-</span>}</td>;
                        })}
                        <td className="px-3 py-2 text-center font-bold text-red-700 border border-gray-500" rowSpan="2">{r.qty}</td>
                      </tr>
                      <tr className="bg-white">
                        {(() => {
                          const cells = []; let sIdx = 0;
                          while (sIdx < activeSizes.length) {
                            const s = activeSizes[sIdx]; const q = r.sizes[s] || 0;
                            if (q === 0) { cells.push(<td key={sIdx} className="border border-gray-500"></td>); sIdx++; continue; }
                            const group = r.rateGroups.find(g => g.sizes.includes(s));
                            if (group) { let span = 0; for (let i = sIdx; i < activeSizes.length; i++) { if (group.sizes.includes(activeSizes[i])) span++; else break; } cells.push(<td key={sIdx} colSpan={span} className="px-2 py-1.5 text-center border border-gray-500"><span className="font-semibold text-blue-800 text-xs">@₹{group.rate.toLocaleString('en-IN')}</span></td>); sIdx += span; }
                            else { cells.push(<td key={sIdx} className="border border-gray-500"></td>); sIdx++; }
                          }
                          return cells;
                        })()}
                      </tr>
                    </React.Fragment>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="bg-[#991b1b] text-white font-bold text-sm">
                  <td className="px-3 py-2.5 border border-red-900" colSpan="4">TOTAL RETURNED</td>
                  {activeSizes.map(s => { const t = rows.reduce((sum, r) => sum + (r.sizes[s] || 0), 0); return <td key={s} className="px-2 py-2.5 text-center border border-red-900">{t || ''}</td>; })}
                  <td className="px-3 py-2.5 text-center border border-red-900">{grandQty}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Lightbox */}
      <Dialog open={!!lightboxImg} onOpenChange={() => setLightboxImg(null)}>
        <DialogContent className="max-w-3xl p-2 bg-black/90 border-none" aria-describedby={undefined}>
          <DialogTitle className="sr-only">Product Image</DialogTitle>
          <img src={lightboxImg} alt="Full size" className="w-full h-auto max-h-[85vh] object-contain rounded" />
        </DialogContent>
      </Dialog>

      {/* Download Dialog */}
      <Dialog open={shareOpen} onOpenChange={setShareOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Download Purchase Return</DialogTitle><DialogDescription>Choose format</DialogDescription></DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="flex gap-3">
              <Button variant={shareFormat === 'pdf' ? 'default' : 'outline'} className={shareFormat === 'pdf' ? 'bg-red-600 hover:bg-red-700' : ''} onClick={() => setShareFormat('pdf')}>PDF</Button>
              <Button variant={shareFormat === 'excel' ? 'default' : 'outline'} className={shareFormat === 'excel' ? 'bg-green-600 hover:bg-green-700' : ''} onClick={() => setShareFormat('excel')}>Excel</Button>
            </div>
            <Button className="w-full" onClick={() => executeShare(shareFormat)} data-testid="pr-share-download">
              <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>Download
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PurchaseReturnDetail;
